import React from 'react';
import styled from 'styled-components';
import {
  attachLocale,
  getSigninUrlWithOrigin,
  resolveLanguageSwitchUrl,
} from '../../../../lib/utils';
import ChevronRight from '../../../svgs/ChevronRight';
import {
  CATEGORIES_DROPDOWN,
  DATA_TRACK_TYPE_INTERNAL,
  PRIMARY_BUTTON_BLUE,
  SIGN_IN_BUTTON,
  LANGUAGE_SWITCH,
  DESKTOP_ONLY,
  MY_ZONE_DROPDOWN,
} from '../../../../lib/constants';
import {
  LightLinkAnchor,
  BoldTitleSpan,
  FlexSpaceBetweenDiv,
  ImgAlignRightSpan,
  LightTitleSpan,
  LightButton,
  MenuItemContainer,
  BorderedMenuItemContainer,
  AirmilesHomeWrapper,
  AirmilesHomeAnchor,
} from './MobileMenuStyles';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import ProfileBalance from '../GlobalNavigation/ProfileBalance';
import Button from '../Button/Button';
import ShopsLogoContainer from '../shared/ShopsLogoContainer';
import BlueRewardsLogo from '../shared/BlueRewardsLogo';
import { getGlobalNavigationAnalytics } from '../GlobalNavigation/globalNavigationAnalytics';
import { getNavigationCategoriesBarAnalytics } from '../NavigationCategoriesBar/navigationCategoriesBarAnalytics';

const TopButtonSectionContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${p => p.theme.unit(3)} 0;
  border-bottom: 1px solid ${p => p.theme.c.border.primary};
`;

const CategoriesNavSlotButtonDiv = styled.div`
  padding: ${p => p.theme.unit(1)} 20px;
  text-decoration: none;
  width: 100%;
  display: flex;
  box-sizing: border-box;
  cursor: pointer;
`;

const BottomMenuSectionLinkContainerDiv = styled.div`
`;

const ProfileBalanceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0 4px 0;
  background-color: ${p => p.theme.c.bg.secondary};
  color: ${p => p.theme.c.text.primary};
`;

const MobileMenuViewOne = (props) => {
  const {
    isUserLoggedIn,
    catNavBarData,
    searchNavBarData,
    visitInfo,
    globalNavData = {},
    locale,
    browseCategoriesOnClick,
    currentPath = '',
    localeUrls,
    subCategoryLocaleSlugMap,
    view,
    theme,
    globalPageConfig,
  } = props;

  const {
    isOneBalanceEnabled = true,
  } = globalPageConfig || {};

  if (!catNavBarData || !searchNavBarData || !globalNavData) {
    return null;
  }

  const airmilesSlot = globalNavData.siteNavSlots && globalNavData.siteNavSlots.length && globalNavData.siteNavSlots[0];
  const languageSlot = globalNavData.anonNavSlots && globalNavData.anonNavSlots.length && globalNavData.anonNavSlots[0];
  const signOutSlot = globalNavData.navSlots && globalNavData.navSlots.length && globalNavData.navSlots.slice(-1)[0];

  let topButtonSection = [];
  let topMenuSectionLinks = [];
  let bottomMenuSectionLinks = [];

  const categoryMenuLink = (link) => ({ link, isGlobalNavigationSlot: false });
  const globalMenuLink = (link) => ({ link, isGlobalNavigationSlot: true });

  /*
   The layout of mobile menu is composed of the following:
   -- Anon view --
   top buttons:
   - category slots 2nd row

   top links section:
   - category slots 1st row

   bottom links section:
   - search nav slots

   -- Signed in view --
   top links section:
   - category slots 1st row

   bottom links section:
   - cat slots 2nd row
   - search nav slots
   */

   //pageStateModifiers.isUserLoggedIn() ? navSlots : anonNavSlots;
  if (isUserLoggedIn()) {
    // User logged in
    topMenuSectionLinks = [...catNavBarData.firstNavSlots]
      .filter(slot => slot.navSlotPurpose !== DESKTOP_ONLY);

    bottomMenuSectionLinks = [
      ...catNavBarData.secondNavSlots.map(categoryMenuLink),
      ...[languageSlot, signOutSlot, airmilesSlot].filter(Boolean).map(globalMenuLink),
    ].filter(({ link }) => ![DESKTOP_ONLY, MY_ZONE_DROPDOWN].includes(link.navSlotPurpose));
  } else {
    // Anon
    topButtonSection = [...globalNavData.anonNavSlots]
      .reverse()
      .filter(slot => slot.navSlotPurpose !== LANGUAGE_SWITCH);
    topMenuSectionLinks = [...catNavBarData.anonFirstNavSlots]
      .filter(slot => slot.navSlotPurpose !== DESKTOP_ONLY);
    bottomMenuSectionLinks = [
      ...catNavBarData.anonSecondNavSlots.map(categoryMenuLink),
      ...[languageSlot, airmilesSlot].filter(Boolean).map(globalMenuLink),
    ];
  }

  const topButtonSectionComponents = topButtonSection.map((button, index) => {
    const {
      buttonStyle,
      url,
      text,
      analyticsSlug,
      navSlotPurpose,
    } = button;

    const resolvedButtonLink = navSlotPurpose === SIGN_IN_BUTTON
      ? getSigninUrlWithOrigin({
        signInUrl: attachLocale(url, locale),
        currentPath,
      })
      : attachLocale(url, locale);
    const track = getGlobalNavigationAnalytics({
      analyticsSlug,
      url: resolvedButtonLink,
    });

    return (
      <CategoriesNavSlotButtonDiv key={`${text}-${index}`}>
        <Button
          variant={(
            buttonStyle === PRIMARY_BUTTON_BLUE
              ? 'primary'
              : 'secondary'
          )}
          fullWidth
          size="large"
          buttonText={text}
          href={track.url}
          dataTrackingId={track.dataTrackingId}
          dataTrack={track.dataTrack}
          dataTrackType={DATA_TRACK_TYPE_INTERNAL}
        />
      </CategoriesNavSlotButtonDiv>
    );
  });

  const topButtonSectionContainer = !isUserLoggedIn() ? (
    <TopButtonSectionContainerDiv>
      <MaxWidthLayout>
        { topButtonSectionComponents }
      </MaxWidthLayout>
    </TopButtonSectionContainerDiv>
  ) : null;

  const topMenuSectionLinkComponents = topMenuSectionLinks.map((link, index) => {
    const {
      url,
      text,
      analyticsSlug,
      showAsDropdown,
      navSlotPurpose,
    } = link;

    const resolvedButtonLink = attachLocale(url, locale);
    // *** ANALYTICS ***
    const track = getNavigationCategoriesBarAnalytics({
      analyticsSlug,
      url: resolvedButtonLink,
    });

    // Browse Categories on click will change the state to proceed to the next view
    if (showAsDropdown && navSlotPurpose === CATEGORIES_DROPDOWN) {
      return (
        <BorderedMenuItemContainer>
          <LightButton
            key={`${text}-${index}`}
            onClick={(event) => browseCategoriesOnClick(event.target)}
            id='mobile-category-browse-categories'
            data-track-id={track.dataTrackingId}
            data-track-click={track.dataTrack}
            data-track-type={DATA_TRACK_TYPE_INTERNAL}
            aria-haspopup="true"
            aria-expanded={view > 1}>
            <FlexSpaceBetweenDiv>
              <BoldTitleSpan>{ text }</BoldTitleSpan>
              <ImgAlignRightSpan>
                <ChevronRight fill={theme.c.icon.primary} />
              </ImgAlignRightSpan>
            </FlexSpaceBetweenDiv>
          </LightButton>
        </BorderedMenuItemContainer>
      );
    }

    return (
      <BorderedMenuItemContainer>
        <LightLinkAnchor
          key={`${text}-${index}`}
          id={track.dataTrackingId}
          data-track-id={track.dataTrackingId}
          data-track-click={track.dataTrack}
          data-track-type={DATA_TRACK_TYPE_INTERNAL}
          href={track.url}>
          <BoldTitleSpan>{ text }</BoldTitleSpan>
        </LightLinkAnchor>
      </BorderedMenuItemContainer>
    );
  });

  const bottomMenuSectionLinkComponents = bottomMenuSectionLinks.map((menuLink, index) => {
    const { link, isGlobalNavigationSlot } = menuLink;
    const {
      url,
      text,
      analyticsSlug,
      descriptiveText,
      navSlotPurpose,
      buttonStyle,
    } = link;

    let localeToUse = url;
    if (url && url.length && url[0] === '/') {
      localeToUse = url.slice(1); // remove the first '/' if exist
    }

    let urlToUse;
    if (navSlotPurpose === LANGUAGE_SWITCH) {
      urlToUse = resolveLanguageSwitchUrl({
        currentPath,
        targetLocale: localeToUse,
        localeUrls,
        subCategoryLocaleSlugMap,
      });
    } else {
      // attach locale to given link
      urlToUse = attachLocale(url, locale);
    }

    const track = isGlobalNavigationSlot
      ? getGlobalNavigationAnalytics({
        analyticsSlug,
        url: urlToUse,
      })
      : getNavigationCategoriesBarAnalytics({
        analyticsSlug,
        url: urlToUse,
      });

    const internalLinkTracking = { 'data-track-type': DATA_TRACK_TYPE_INTERNAL };

    const special = (buttonStyle && buttonStyle === 'PRIMARY_BUTTON_BLUE');

    // *** ANALYTICS ***
    if (special) {
      return (
        <AirmilesHomeWrapper>
          <AirmilesHomeAnchor
            key={`${descriptiveText}-${index}`}
            id={track.dataTrackingId}
            data-track-id={track.dataTrackingId}
            data-track-click={track.dataTrack}
            {...internalLinkTracking}
            href={track.url}
          >
            <BlueRewardsLogo text={descriptiveText} locale={locale} />
          </AirmilesHomeAnchor>
        </AirmilesHomeWrapper>
      );
    }
    return (
      <MenuItemContainer>
        <LightLinkAnchor
          key={`${text}-${index}`}
          id={track.dataTrackingId}
          data-track-id={track.dataTrackingId}
          data-track-click={track.dataTrack}
          {...internalLinkTracking}
          href={track.url}
        >
          <LightTitleSpan>{ text }</LightTitleSpan>
        </LightLinkAnchor>
      </MenuItemContainer>
    );
  });

  const bottomMenuSectionLinkContainer = (
    <BottomMenuSectionLinkContainerDiv>
      { bottomMenuSectionLinkComponents }
    </BottomMenuSectionLinkContainerDiv>
  );

  return (
    <div>
      { isUserLoggedIn() && (
        <div>
          <ProfileBalanceContainer>
            <ProfileBalance
              isMobile
              visitInfo={visitInfo}
              profileBalanceNavSlot={globalNavData.balanceNavSlots}
              globalPageConfig={globalPageConfig}
            />
          </ProfileBalanceContainer>
        </div>
      )}
      {topButtonSectionContainer}
      {topMenuSectionLinkComponents}
      {bottomMenuSectionLinkContainer}
    </div>
  );
};

export default MobileMenuViewOne;
